/**
 * Шаблоны ТЗ.
 *
 * Шаблон — не просто ярлык, а набор значений по умолчанию. Благодаря
 * ему создание типового ТЗ занимает два действия вместо восьми полей.
 * Цены правятся прямо в таблице: отдельный экран ради двух чисел избыточен.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Page, PageHeader } from '../components/Layout';
import { Field, Loading, toast } from '../components/ui';
import { money } from '../lib/format';
import type { Template } from '../lib/types';

export function TemplatesPage() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: '', shoot: 500, edit: 150 });

  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<Template[]>('/api/templates'),
  });

  const save = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.patch(`/api/templates/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
      toast('Сохранено');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  /**
   * Шаблон не удаляется физически: на него ссылаются созданные ТЗ,
   * и удаление порвало бы их историю. Сервер просто выключает его —
   * из списка выбора шаблон пропадает, старые ТЗ остаются целыми.
   */
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/templates/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
      toast('Шаблон убран из списка');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/templates', {
        name: draft.name,
        defaultPriceShoot: draft.shoot,
        defaultPriceEdit: draft.edit,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
      setAdding(false);
      setDraft({ name: '', shoot: 500, edit: 150 });
      toast('Шаблон добавлен');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  return (
    <>
      <PageHeader
        title="Шаблоны ТЗ"
        sub={`${data?.length ?? 0} шаблонов`}
        actions={
          <button className="btn btn-primary" onClick={() => setAdding((v) => !v)}>
            + Добавить
          </button>
        }
      />
      <Page>
        {adding && (
          <div className="card mb-4 max-w-lg">
            <Field label="Название">
              <input
                className="input uppercase"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="ОГНЕННЫЕ КОЛЬЦА"
                autoFocus
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Цена съёмки, ₽">
                <input
                  className="input mono"
                  type="number"
                  value={draft.shoot}
                  onChange={(e) => setDraft({ ...draft, shoot: Number(e.target.value) })}
                />
              </Field>
              <Field label="Цена монтажа, ₽">
                <input
                  className="input mono"
                  type="number"
                  value={draft.edit}
                  onChange={(e) => setDraft({ ...draft, edit: Number(e.target.value) })}
                />
              </Field>
            </div>
            <button
              className="btn btn-primary w-full"
              onClick={() => create.mutate()}
              disabled={!draft.name || create.isPending}
            >
              Создать шаблон
            </button>
          </div>
        )}

        {isLoading ? (
          <Loading rows={5} />
        ) : (
          <div className="card p-0 overflow-hidden">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="text-[10.5px] tracking-[0.11em] text-muted2 font-bold uppercase">
                  <th className="text-left px-4 pt-4 pb-2.5 border-b border-line">Название</th>
                  <th className="text-right px-4 pt-4 pb-2.5 border-b border-line w-36">Съёмка</th>
                  <th className="text-right px-4 pt-4 pb-2.5 border-b border-line w-36">Монтаж</th>
                  <th className="text-right px-4 pt-4 pb-2.5 border-b border-line">Итого</th>
                  <th className="text-right px-4 pt-4 pb-2.5 border-b border-line w-28">Срок</th>
                  <th className="w-12 px-4 pt-4 pb-2.5 border-b border-line" />
                </tr>
              </thead>
              <tbody>
                {data?.map((t) => (
                  <tr key={t.id}>
                    <td className="px-4 py-3 border-b border-[#1A1A24]">
                      <b className="text-sm">{t.name}</b>
                    </td>
                    <td className="px-4 py-3 border-b border-[#1A1A24]">
                      <PriceInput
                        value={t.defaultPriceShoot / 100}
                        onSave={(v) =>
                          save.mutate({ id: t.id, patch: { defaultPriceShoot: v } })
                        }
                      />
                    </td>
                    <td className="px-4 py-3 border-b border-[#1A1A24]">
                      <PriceInput
                        value={t.defaultPriceEdit / 100}
                        onSave={(v) => save.mutate({ id: t.id, patch: { defaultPriceEdit: v } })}
                      />
                    </td>
                    <td className="px-4 py-3 border-b border-[#1A1A24] text-right mono font-bold">
                      {money(t.defaultPriceShoot + t.defaultPriceEdit)}
                    </td>
                    <td className="px-4 py-3 border-b border-[#1A1A24] text-right text-muted text-xs">
                      {t.defaultShootHours} ч / {t.defaultEditHours} ч
                    </td>
                    <td className="px-4 py-3 border-b border-[#1A1A24] text-right">
                      <button
                        className="text-muted2 hover:text-dang transition-colors text-lg leading-none px-1"
                        title="Убрать шаблон из списка"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (confirm(`Убрать шаблон «${t.name}» из списка?`)) remove.mutate(t.id);
                        }}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-[11.5px] text-muted2 mt-3.5 px-1 leading-relaxed max-w-2xl">
          Изменение цены влияет только на новые ТЗ. В уже созданных цена зафиксирована
          снимком — иначе правка шаблона задним числом меняла бы деньги по выполненной работе.
          <br />
          Удаление тоже не затрагивает прошлое: шаблон просто убирается из списка выбора,
          а созданные по нему ТЗ остаются нетронутыми.
        </div>
      </Page>
    </>
  );
}

/** Цена правится по месту: клик, ввод, Enter или уход фокуса */
function PriceInput({ value, onSave }: { value: number; onSave: (v: number) => void }) {
  const [local, setLocal] = useState(String(value));

  return (
    <input
      className="input mono text-right py-1.5"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        const n = Number(local);
        if (!Number.isNaN(n) && n !== value) onSave(n);
        else setLocal(String(value));
      }}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
    />
  );
}
